import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, firstValueFrom, map, Observable, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { RefreshResponse, Session } from '../../models';
import { SessionService } from '../local/session.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly sessionService = inject(SessionService);
  private readonly baseUrl = `${environment.apiUrl}/v1/auth`;

  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  login(email: string, password: string): Observable<Session> {
    return this.http
      .post<Session>(`${this.baseUrl}/login`, { email, password }, { withCredentials: true })
      .pipe(
        tap(session => {
          this.sessionService.set(session);
          this.scheduleRefresh(session.expiresIn);
        }),
      );
  }

  changePassword(passwordActual: string, passwordNuevo: string): Promise<void> {
    return firstValueFrom(
      this.http.put<void>(`${this.baseUrl}/password`, { passwordActual, passwordNuevo })
    );
  }

  logout(): void {
    this.clearRefreshTimer();
    this.http.post(`${this.baseUrl}/logout`, {}, { withCredentials: true }).subscribe();
    this.sessionService.clear();
  }

  // Called on app init to restore session from the httpOnly refresh cookie.
  tryRestoreSession(): Observable<void> {
    return this.http
      .post<RefreshResponse>(`${this.baseUrl}/refresh`, {}, { withCredentials: true })
      .pipe(
        tap(({ accessToken, expiresIn }) => {
          const restored = this.sessionService.restoreFromStorage(accessToken, expiresIn);
          if (restored) this.scheduleRefresh(expiresIn);
        }),
        map(() => void 0),
        catchError(() => {
          this.sessionService.clear();
          return of(void 0);
        }),
      );
  }

  private refresh(): Observable<void> {
    return this.http
      .post<RefreshResponse>(`${this.baseUrl}/refresh`, {}, { withCredentials: true })
      .pipe(
        tap(({ accessToken, expiresIn }) => {
          this.sessionService.updateTokens(accessToken, expiresIn);
          this.scheduleRefresh(expiresIn);
        }),
        map(() => void 0),
        catchError(() => {
          this.sessionService.clear();
          return of(void 0);
        }),
      );
  }

  scheduleRefreshPublic(expiresIn: number): void {
    this.scheduleRefresh(expiresIn);
  }

  private scheduleRefresh(expiresIn: number): void {
    this.clearRefreshTimer();
    // Refresh 30 s before expiry so there is no gap in authentication.
    const delayMs = Math.max((expiresIn - 30) * 1000, 0);
    this.refreshTimer = setTimeout(() => this.refresh().subscribe(), delayMs);
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
}
